import React, { useEffect, useMemo, useState } from 'react';
import {
  BriefcaseBusiness,
  Clock3,
  History,
  X,
} from 'lucide-react';
import ModalShell from './ModalShell';
import { CompactListSkeleton } from './Skeleton';
import useModalBackButton from '../hooks/useModalBackButton';
import * as servicesApi from '../services/servicesApi';
import { formatTimezoneDate } from '../utils/timezone';
import { haptic } from '../utils/vibration';
import { getServiceIconLabel } from '../utils/serviceIcons';

const ACTION_LABELS = {
  created: 'Создание',
  activated: 'Активация',
  deactivated: 'Деактивация',
  name_changed: 'Изменение',
  group_changed: 'Изменение',
  unit_changed: 'Изменение',
  icon_changed: 'Изменение',
  description_changed: 'Изменение',
  processing_route_changed: 'Изменение',
  price_set: 'Установка цены',
  price_changed: 'Изменение цены',
  price_ended: 'Завершение цены',
  department_price_set: 'Спец. цена',
};

const SERVICE_TYPE_LABELS = {
  washing: 'Стирка',
  cleaning: 'Химчистка мебели',
  extra_washing: 'Доп. услуги стирка',
  extra_cleaning: 'Доп. услуги химчистка мебели',
  extra_order: 'Доп. услуги заказа',
};

const CLIENT_TYPE_LABELS = {
  b2c: 'Физ. лица',
  b2b: 'Организации',
  all: 'Все клиенты',
};

const UNIT_LABELS = {
  'шт': 'шт.',
  'кг': 'кг.',
  'м2': 'м²',
};

const PROCESSING_ROUTE_LABELS = {
  laundry: 'Прачечная',
  cleaning: 'Чистка',
};

const safeParse = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

const formatPriceValue = (price, unitType = 'rub') => {
  if (price === null || price === undefined || price === '') return '—';
  return `${price}${unitType === 'percent' ? '%' : ' ₽'}`;
};

const formatValue = (key, value, fullRecord = {}) => {
  if (value === null || value === undefined || value === '') return '—';

  if (key === 'service_type') return SERVICE_TYPE_LABELS[String(value).trim()] || String(value);
  if (key === 'client_type') return CLIENT_TYPE_LABELS[String(value).trim()] || String(value);
  if (key === 'unit') return UNIT_LABELS[String(value).trim()] || String(value);
  if (key === 'processing_route') return PROCESSING_ROUTE_LABELS[String(value).trim()] || String(value);
  if (key === 'icon') return getServiceIconLabel(value);
  if (key === 'active') return value ? 'Активна' : 'Неактивна';
  if (key === 'price' || key === 'base_price') return formatPriceValue(value, fullRecord.unit_type);
  if (key === 'effective_from' || key === 'effective_to') return formatTimezoneDate(value, false);
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
  return String(value).trim() || '—';
};

const buildGenericRows = (oldValue, newValue, fieldMap = {}) => {
  const before = oldValue && typeof oldValue === 'object' && !Array.isArray(oldValue) ? oldValue : {};
  const after = newValue && typeof newValue === 'object' && !Array.isArray(newValue) ? newValue : {};
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));

  return keys
    .filter((key) => !['group_id', 'replace_scheduled'].includes(key))
    .filter((key) => JSON.stringify(before[key] ?? null) !== JSON.stringify(after[key] ?? null))
    .map((key) => ({
      key,
      label: fieldMap[key] || key,
      oldValue: before[key],
      newValue: after[key],
      oldRecord: before,
      newRecord: after,
    }));
};

const buildChangeRows = (log, oldValue, newValue) => {
  switch (log.action) {
    case 'created':
      return buildGenericRows({}, newValue, {
        name: 'Название',
        service_type: 'Тип услуги',
        processing_route: 'Маршрут обработки',
        unit: 'Ед. измерения',
        icon: 'Иконка',
        base_price: 'Базовая цена',
        description: 'Описание',
      });
    case 'name_changed':
      return [{
        key: 'name',
        label: 'Название',
        oldValue: oldValue?.name,
        newValue: newValue?.name,
      }];
    case 'group_changed':
      return [{
        key: 'group_name',
        label: 'Группа',
        oldValue: oldValue?.name,
        newValue: newValue?.name,
      }];
    case 'unit_changed':
      return [{
        key: 'unit',
        label: 'Ед. измерения',
        oldValue: oldValue?.unit,
        newValue: newValue?.unit,
      }];
    case 'icon_changed':
      return [{
        key: 'icon',
        label: 'Иконка',
        oldValue: oldValue?.icon,
        newValue: newValue?.icon,
      }];
    case 'description_changed':
      return [{
        key: 'description',
        label: 'Описание',
        oldValue: oldValue?.description,
        newValue: newValue?.description,
      }];
    case 'processing_route_changed':
      return [{
        key: 'processing_route',
        label: 'Маршрут обработки',
        oldValue: oldValue?.processing_route,
        newValue: newValue?.processing_route,
      }];
    case 'activated':
    case 'deactivated':
      return [{
        key: 'active',
        label: 'Активность',
        oldValue: oldValue?.active,
        newValue: newValue?.active,
      }];
    case 'price_set':
    case 'price_changed':
      return buildGenericRows(oldValue, newValue, {
        price: 'Цена',
        client_type: 'Тип клиента',
        effective_from: 'Дата начала действия',
        comment: 'Комментарий',
      });
    case 'price_ended':
      return [
        {
          key: 'price',
          label: 'Цена',
          oldValue: oldValue?.price,
          newValue: null,
          oldRecord: oldValue || {},
          newRecord: {},
        },
        {
          key: 'effective_to',
          label: 'Дата окончания действия',
          oldValue: null,
          newValue: oldValue?.effective_to,
        },
      ].filter((row) => row.oldValue !== null || row.newValue !== null);
    case 'department_price_set':
      return buildGenericRows(oldValue, newValue, {
        department_name: 'Подразделение',
        price: 'Цена',
        client_type: 'Тип клиента',
        effective_from: 'Дата начала действия',
        comment: 'Комментарий',
      });
    default:
      return buildGenericRows(oldValue, newValue);
  }
};

const summarizeLog = (log, oldValue, newValue, changeRows) => {
  if (log.action === 'created') {
    return `Создана услуга «${newValue?.name || `#${log.service_id}`}»`;
  }
  if (log.action === 'price_set') {
    return `Установлена цена: ${formatPriceValue(newValue?.price, newValue?.unit_type)}`;
  }
  if (log.action === 'price_changed') {
    const value = formatPriceValue(newValue?.price, newValue?.unit_type);
    const dateLabel = newValue?.effective_from ? ` с ${formatTimezoneDate(newValue.effective_from, false)}` : '';
    return `Запланирована цена: ${value}${dateLabel}`;
  }
  if (log.action === 'price_ended') {
    return `Завершено действие цены ${formatPriceValue(oldValue?.price, oldValue?.unit_type)}`;
  }
  if (log.action === 'group_changed') {
    return `Группа: ${formatValue('group_name', newValue?.name)}`;
  }
  if (log.action === 'department_price_set') {
    return `Спец. цена для ${newValue?.department_name || 'подразделения'}: ${formatPriceValue(newValue?.price, newValue?.unit_type)}`;
  }
  if (changeRows.length > 0) {
    const firstRow = changeRows[0];
    return `${firstRow.label}: ${formatValue(firstRow.key, firstRow.newValue, firstRow.newRecord || {})}`;
  }
  return ACTION_LABELS[log.action] || log.action || 'Изменения сохранены';
};

const ServiceActivityModal = ({ open, onClose, service }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useModalBackButton(open, onClose);

  useEffect(() => {
    if (!open || !service?.id) return undefined;

    let cancelled = false;

    const loadHistory = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await servicesApi.getServiceAudit(service.id);
        if (cancelled) return;
        setLogs(Array.isArray(data) ? data : []);
      } catch (err) {
        if (cancelled) return;
        console.error('Ошибка загрузки истории услуги:', err);
        setError(err.message || 'Не удалось загрузить историю услуги');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [open, service?.id]);

  const visibleLogs = useMemo(() => (
    logs.map((log) => {
      const oldValue = safeParse(log.old_value);
      const newValue = safeParse(log.new_value);
      const changeRows = buildChangeRows(log, oldValue, newValue);
      return {
        ...log,
        oldValue,
        newValue,
        changeRows,
        summary: summarizeLog(log, oldValue, newValue, changeRows),
      };
    })
  ), [logs]);

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      variant="sheet"
      expandable={true}
      panelClassName="max-h-[88vh] overflow-hidden rounded-t-xl sm:max-w-3xl sm:rounded-xl"
    >
      <div className="flex max-h-[88vh] flex-col">
        <div className="border-b border-gray-200 px-4 py-4 dark:border-gray-800">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-base font-semibold text-gray-900 dark:text-gray-100">История услуги</div>
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {service?.name || `Услуга #${service?.id}`}
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                haptic('close');
                onClose();
              }}
              className="rounded-lg bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-4 py-4">
          {loading ? (
            <CompactListSkeleton count={4} />
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
              {error}
            </div>
          ) : visibleLogs.length === 0 ? (
            <div className="py-10 text-center">
              <History className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
              <div className="mt-3 text-sm font-medium text-gray-900 dark:text-white">История пока пуста</div>
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Для этой услуги ещё нет сохранённых изменений.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {visibleLogs.map((log) => (
                <div
                  key={log.id}
                  className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300">
                      <BriefcaseBusiness className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {log.creator_name || 'Неизвестно'}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400">
                          {ACTION_LABELS[log.action] || log.action || 'Изменение'}
                        </span>
                      </div>

                      <div className="mt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                        <Clock3 className="h-3.5 w-3.5" />
                        <span>{formatTimezoneDate(log.created_date, true)}</span>
                      </div>

                      <div className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-200">
                        {log.summary}
                      </div>

                      {log.comment ? (
                        <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:border-gray-800 dark:bg-gray-800/40 dark:text-gray-300">
                          {log.comment}
                        </div>
                      ) : null}

                      {log.changeRows.length > 0 ? (
                        <details className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-800 dark:bg-gray-800/40">
                          <summary className="cursor-pointer list-none text-xs font-medium text-gray-700 dark:text-gray-200">
                            Что изменили
                          </summary>
                          <div className="mt-2 space-y-2">
                            {log.changeRows.map((row) => (
                              <div key={`${log.id}-${row.key}-${row.label}`} className="rounded-lg bg-white px-3 py-2 text-xs dark:bg-gray-900">
                                <div className="font-medium text-gray-500 dark:text-gray-400">{row.label}</div>
                                <div className="mt-1 grid gap-1 sm:grid-cols-2">
                                  <div className="min-w-0 text-gray-600 dark:text-gray-300">
                                    <span className="mb-1 block text-gray-400">Было:</span>
                                    <span className="whitespace-pre-wrap break-words">
                                      {formatValue(row.key, row.oldValue, row.oldRecord || {})}
                                    </span>
                                  </div>
                                  <div className="min-w-0 text-gray-900 dark:text-gray-100">
                                    <span className="mb-1 block text-gray-400">Стало:</span>
                                    <span className="whitespace-pre-wrap break-words">
                                      {formatValue(row.key, row.newValue, row.newRecord || {})}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </details>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ModalShell>
  );
};

export default ServiceActivityModal;
